import React, { useEffect } from 'react'
import type { WorkflowNodeData } from '@/types/workflow'

interface ActionConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import { inputClass, inputStyle, sectionClass, textareaClass, infoBannerClass } from './_kit'
import { cn } from '@/lib/utils'
import { Select } from '@/components/ui/Select'
import { Field } from '@/components/ui/Field'
import { ContactListCombobox, NO_LIST, type ContactListSelection } from '@/components/ui/ContactListCombobox'
import { useContactStore } from '@/stores/contactStore'
import { ACTION_TYPE_GROUPS, ACTION_TYPE_LABELS } from '@/lib/workflowActions'

/**
 * "Move to List" config — search an existing contact list or type a new name.
 * Picking an existing list stores { list_id, list_name }; a new name stores
 * { list_name } only, and the backend get-or-creates the list by name the
 * first time a contact reaches the node (no orphan/empty lists).
 */
const MoveToListFields: React.FC<{
  actionConfig: Record<string, any>
  updateConfig: (key: string, value: any) => void
  replaceConfig: (cfg: Record<string, any>) => void
}> = ({ actionConfig, replaceConfig }) => {
  const { contactLists, fetchContactLists } = useContactStore()

  useEffect(() => {
    if (contactLists.length === 0) fetchContactLists()
  }, [])

  const value: ContactListSelection =
    actionConfig.list_id != null
      ? { mode: 'existing', id: Number(actionConfig.list_id), name: actionConfig.list_name || '' }
      : actionConfig.list_name
        ? { mode: 'new', name: actionConfig.list_name }
        : NO_LIST

  const handleSelect = (sel: ContactListSelection) => {
    if (sel.mode === 'existing') replaceConfig({ list_id: sel.id, list_name: sel.name })
    else if (sel.mode === 'new') replaceConfig({ list_name: sel.name })
    else replaceConfig({})
  }

  return (
    <div className="space-y-3">
      {/* The combobox owns its own input and takes no `id`, so the label names a
          group; its `ariaLabel` still names the input itself. */}
      <Field
        label="Contact List"
        as="group"
        hint={
          value.mode === 'new'
            ? `A new list "${value.name}" is created the first time a contact reaches this step.`
            : undefined
        }
      >
        <ContactListCombobox
          lists={contactLists.map((l) => ({ id: l.id, name: l.name }))}
          value={value}
          onChange={handleSelect}
          allowCreate
          placeholder="Search a list, or type a new name…"
          ariaLabel="Contact List"
        />
      </Field>
    </div>
  )
}

const FIELD_OPTIONS = [
  { value: 'lead_status', label: 'Lead Status' },
  { value: 'seniority_level', label: 'Seniority Level' },
  { value: 'department', label: 'Department' },
]

const MODE_OPTIONS = [
  { value: 'increment', label: 'Increment By' },
  { value: 'decrement', label: 'Decrement By' },
  { value: 'set', label: 'Set To' },
]

// Built from the shared source of truth so labels match every other surface.
const actionOptions = ACTION_TYPE_GROUPS.map((g) => ({
  group: g.group,
  options: g.values.map((value) => ({ value, label: ACTION_TYPE_LABELS[value] })),
}))

const ActionConfig: React.FC<ActionConfigProps> = ({ data, updateField }) => {
  const actionType = data.action_type || ''
  const actionConfig = data.action_config || {}

  const updateConfig = (key: string, value: any) => {
    updateField('action_config', { ...actionConfig, [key]: value })
  }

  // Replace the whole action_config (used by the list picker so switching
  // between an existing list and "create new" never leaves a stale list_id).
  const replaceConfig = (cfg: Record<string, any>) => {
    updateField('action_config', cfg)
  }

  const renderActionFields = () => {
    switch (actionType) {
      case 'tag_contact':
      case 'remove_tag':
        return (
          <Field label="Tag Name">
            {(f) => (
              <input
                {...f}
                type="text"
                value={actionConfig.tag || ''}
                onChange={(e) => updateConfig('tag', e.target.value)}
                placeholder="e.g., engaged, vip"
                className={inputClass} style={inputStyle}
              />
            )}
          </Field>
        )
      case 'move_to_list':
        return (
          <MoveToListFields
            actionConfig={actionConfig}
            updateConfig={updateConfig}
            replaceConfig={replaceConfig}
          />
        )
      case 'update_field':
        return (
          <>
            <Field label="Field Name">
              {(f) => (
                <Select
                  {...f}
                  value={actionConfig.field || ''}
                  onChange={(value) => updateConfig('field', value)}
                  options={FIELD_OPTIONS}
                  placeholder="Select field..."
                  className="w-full"
                />
              )}
            </Field>
            <Field label="New Value">
              {(f) => (
                <input
                  {...f}
                  type="text"
                  value={actionConfig.value || ''}
                  onChange={(e) => updateConfig('value', e.target.value)}
                  placeholder="Enter new value..."
                  className={inputClass} style={inputStyle}
                />
              )}
            </Field>
          </>
        )
      case 'update_lead_score':
        return (
          <>
            <Field label="Mode">
              {(f) => (
                <Select
                  {...f}
                  value={actionConfig.mode || 'increment'}
                  onChange={(value) => updateConfig('mode', value)}
                  options={MODE_OPTIONS}
                  className="w-full"
                />
              )}
            </Field>
            <Field label="Value">
              {(f) => (
                <input
                  {...f}
                  type="number"
                  value={actionConfig.score_value || ''}
                  onChange={(e) => updateConfig('score_value', parseInt(e.target.value) || 0)}
                  placeholder="10"
                  className={inputClass} style={inputStyle}
                />
              )}
            </Field>
          </>
        )
      case 'send_webhook':
        return (
          <>
            <Field label="Webhook URL">
              {(f) => (
                <input
                  {...f}
                  type="url"
                  value={actionConfig.url || ''}
                  onChange={(e) => updateConfig('url', e.target.value)}
                  placeholder="https://..."
                  className={inputClass} style={inputStyle}
                />
              )}
            </Field>
            <Field label="Payload (JSON)">
              {(f) => (
                <textarea
                  {...f}
                  value={actionConfig.payload || ''}
                  onChange={(e) => updateConfig('payload', e.target.value)}
                  placeholder='{"event": "workflow_action"}'
                  rows={3}
                  className={cn(textareaClass, 'h-[96px] overflow-y-auto font-mono text-xs')}
                />
              )}
            </Field>
          </>
        )
      case 'send_notification':
        return (
          <Field label="Notification Message">
            {(f) => (
              <textarea
                {...f}
                value={actionConfig.message || ''}
                onChange={(e) => updateConfig('message', e.target.value)}
                placeholder="Contact {{name}} has..."
                rows={2}
                className={cn(textareaClass, 'h-[96px] overflow-y-auto')}
                style={inputStyle}
              />
            )}
          </Field>
        )
      case 'mark_as_qualified':
      case 'mark_as_unqualified':
        return (
          <div className={infoBannerClass}>
            <p className="text-xs">
              This will update the contact's lead status to <strong>{actionType === 'mark_as_qualified' ? 'Qualified' : 'Unqualified'}</strong>.
            </p>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className={sectionClass}>
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="e.g., Tag as Engaged"
            className={inputClass} style={inputStyle}
          />
        )}
      </Field>
      <Field label="Action Type">
        {(f) => (
          <Select
            {...f}
            value={actionType}
            onChange={(value) => {
              updateField('action_type', value)
              updateField('action_config', {})
            }}
            groups={actionOptions.map(g => ({ label: g.group, options: g.options }))}
            placeholder="Select action..."
            className="w-full"
          />
        )}
      </Field>
      {renderActionFields()}
    </div>
  )
}

export default ActionConfig
